import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye } from "lucide-react";
import { api, formatApiError, ROLE_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichText } from "@/components/glossary/Glossary";
import { DocViewer, fmtDT } from "@/components/legal/LegalParts";
import { downloadPdf } from "@/pages/MyAgreementsPage";

const err = (x) => toast.error(formatApiError(x.response?.data?.detail));
export const ROLES = ["BROKER", "ACCOUNTANT", "CONSULTANT"];
export const STAGE = { REGISTRATION: "Kayıt sırasında", FIRST_LOGIN: "İlk girişte" };

export function MetaForm({ value, onChange, kind }) {
  const set = (p) => onChange({ ...value, ...p });
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Ne zaman gösterilsin?</Label>
          <Select value={value.stage} onValueChange={(v) => set({ stage: v })}>
            <SelectTrigger data-testid="legal-meta-stage"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="REGISTRATION">Kayıt sırasında (broker)</SelectItem><SelectItem value="FIRST_LOGIN">İlk girişte</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Sıra</Label><Input type="number" value={value.order} onChange={(e) => set({ order: Number(e.target.value) || 0 })} data-testid="legal-meta-order" /></div>
      </div>
      <div>
        <Label>Kimler onaylasın?</Label>
        <div className="flex gap-2 flex-wrap mt-1">
          {ROLES.map((r) => {
            const on = value.audience.includes(r);
            return <button key={r} type="button" data-testid={`legal-meta-aud-${r}`} onClick={() => set({ audience: on ? value.audience.filter((x) => x !== r) : [...value.audience, r] })}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{ROLE_LABEL[r]}</button>;
          })}
        </div>
        {value.stage === "REGISTRATION" && <div className="text-[11px] text-slate-400 mt-1">Kayıt formunda broker için gösterilir; diğer seçili roller ilk girişte onaylar.</div>}
      </div>
      <div className="flex gap-6 flex-wrap">
        {kind !== "CONSENT" && <label className="flex items-center gap-2 text-sm"><Switch checked={value.required} onCheckedChange={(v) => set({ required: v })} data-testid="legal-meta-required" />Zorunlu</label>}
        <label className="flex items-center gap-2 text-sm"><Switch checked={value.active} onCheckedChange={(v) => set({ active: v })} data-testid="legal-meta-active" />Aktif</label>
      </div>
    </div>
  );
}

export function TextEditor({ doc, onChanged }) {
  const src = doc.draft || doc.current || { title: "", summary: "", body: "" };
  const [f, setF] = useState({ title: src.title, summary: src.summary || "", body: src.body || "" });
  const [pub, setPub] = useState({ change_note: "", require_reaccept: true });
  const [preview, setPreview] = useState(false);
  const saveDraft = async () => { try { await api.put(`/admin/legal/docs/${doc.id}/draft`, f); toast.success("Taslak kaydedildi"); onChanged(); } catch (x) { err(x); } };
  const publish = async () => {
    if (!window.confirm("Yeni sürüm yayımlansın mı? Yayımlanan sürüm değiştirilemez.")) return;
    try { await api.put(`/admin/legal/docs/${doc.id}/draft`, f); const { data } = await api.post(`/admin/legal/docs/${doc.id}/publish`, pub); toast.success(`v${data.version} yayımlandı`); onChanged(); }
    catch (x) { err(x); }
  };
  return (
    <div className="space-y-3" data-testid="legal-text-editor">
      {doc.draft && <Badge variant="outline" className="border-amber-300 text-amber-800">Yayımlanmamış taslak var</Badge>}
      <div><Label>Başlık</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="legal-text-title" /></div>
      <div><Label>Kısa açıklama</Label><Input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="legal-text-summary" /></div>
      <div>
        <div className="flex justify-between"><Label>Metin</Label><button type="button" className="text-xs text-emerald-800 hover:underline" onClick={() => setPreview(!preview)} data-testid="legal-text-preview">{preview ? "Düzenle" : "Önizle"}</button></div>
        {preview ? <div className="border rounded-lg p-4 max-h-80 overflow-y-auto"><RichText text={f.body} /></div>
          : <Textarea rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="legal-text-body" />}
        <div className="text-[11px] text-slate-400 mt-1">"## " başlık, "- " madde, **kalın** yazı.</div>
      </div>
      <div className="rounded-lg border p-3 space-y-2 bg-slate-50">
        <Input placeholder="Değişiklik notu (ör. ücret maddesi güncellendi)" value={pub.change_note} onChange={(e) => setPub({ ...pub, change_note: e.target.value })} data-testid="legal-publish-note" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={pub.require_reaccept} onCheckedChange={(v) => setPub({ ...pub, require_reaccept: v })} data-testid="legal-publish-reaccept" />Kullanıcılardan yeniden onay iste</label>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={saveDraft} data-testid="legal-save-draft">Taslağı kaydet</Button>
        <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={publish} data-testid="legal-publish-btn">Yeni sürüm yayımla (v{(doc.current_version || 0) + 1})</Button>
      </div>
    </div>
  );
}

export function VersionsPanel({ docId }) {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => { api.get(`/admin/legal/docs/${docId}/versions`).then(({ data }) => setItems(data)).catch(err); }, [docId]);
  return (
    <div className="divide-y" data-testid="legal-versions-panel">
      {items.map((v) => (
        <div key={v.id} className="py-2 flex items-center gap-3 flex-wrap text-sm" data-testid={`legal-version-v${v.version}`}>
          <span className="font-mono font-bold w-8">v{v.version}</span>
          <span className="flex-1 min-w-[160px]">{v.title}<div className="text-[11px] text-slate-500">{fmtDT(v.published_at)} · {v.published_by}{v.change_note && ` · ${v.change_note}`}{v.requires_reaccept && " · yeniden onay istendi"}</div></span>
          <span className="font-mono text-[10px] text-slate-400 hidden md:inline">{v.content_hash.slice(0, 16)}…</span>
          <Button size="sm" variant="ghost" onClick={() => setView(v)} data-testid={`legal-version-view-v${v.version}`}><Eye className="w-4 h-4" /></Button>
        </div>
      ))}
      <DocViewer doc={view} onClose={() => setView(null)} />
    </div>
  );
}

export function ReportPanel({ docId }) {
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/admin/legal/docs/${docId}/report`).then(({ data }) => setR(data)).catch(err); }, [docId]);
  if (!r) return <div className="text-sm text-slate-500">Yükleniyor…</div>;
  if (r.events) return (
    <div className="space-y-3" data-testid="legal-report-panel">
      <div className="flex gap-3 text-sm"><Badge className="bg-emerald-700">İzin veren: {r.granted}</Badge><Badge variant="outline">Vermeyen: {r.denied}</Badge><Badge variant="outline">Karar vermeyen: {r.undecided}</Badge></div>
      <div className="max-h-80 overflow-y-auto divide-y text-xs">
        {r.events.map((e) => <div key={e.id} className="py-1.5 flex gap-3 flex-wrap"><b className="w-24">{e.granted ? "Verildi" : "Verilmedi/geri alındı"}</b><span>{e.user_name || "(başvuru)"}</span><span className="text-slate-500">{e.office_name}</span><span>{fmtDT(e.at)}</span><span>v{e.version}</span><span className="font-mono text-slate-400">{e.ip}</span></div>)}
      </div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="legal-report-panel">
      <div className="flex gap-2 flex-wrap text-sm">
        {Object.entries(r.per_version).map(([v, n]) => <Badge key={v} variant="outline" data-testid={`legal-report-count-v${v}`}>v{v}: {n} onay</Badge>)}
        <Badge variant="outline" className="border-amber-300 text-amber-800" data-testid="legal-report-pending-count">Bekleyen: {r.pending.length} kullanıcı</Badge>
      </div>
      {r.pending.length > 0 && (
        <div><div className="text-xs font-semibold text-slate-700 mb-1">Onay bekleyen kullanıcılar</div>
          <div className="max-h-40 overflow-y-auto text-xs divide-y">{r.pending.map((u) => <div key={u.id} className="py-1 flex gap-3"><span>{u.full_name}</span><span className="text-slate-500">{u.username}</span><span>{ROLE_LABEL[u.role]}</span><span className="text-slate-500">{u.office_name}</span>{u.accepted_version && <span>son onay v{u.accepted_version}</span>}</div>)}</div>
        </div>
      )}
      <div><div className="text-xs font-semibold text-slate-700 mb-1">Onay kayıtları</div>
        <div className="max-h-80 overflow-y-auto text-xs divide-y">
          {r.acceptances.map((a) => (
            <div key={a.id} className="py-1.5 flex gap-3 flex-wrap items-center" data-testid={`legal-report-row-${a.id}`}>
              <b>v{a.version}</b><span>{a.user_name || "(başvuru)"}</span><span className="text-slate-500">{a.office_name}</span><span>{ROLE_LABEL[a.role]}</span><span>{fmtDT(a.at)}</span><span>{a.accept_type}</span><span className="font-mono text-slate-400">{a.ip}</span>
              <button className="ml-auto text-emerald-800 hover:underline flex items-center gap-1" onClick={() => downloadPdf(a.id)}><Download className="w-3 h-3" />PDF</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
